import { Shell } from "@/components/layout/Shell";
import { useSeo } from "@/hooks/use-seo";
import { Link } from "wouter";
import { motion } from "framer-motion";

const GRAVATAR =
  "https://www.gravatar.com/avatar/96f84f2420af101ffa7470034e5c23ec?s=400";

const TOOLS = [
  { icon: "🎨", label: "Personality Read", href: "/quiz/personality", desc: "Discover your emotional baseline through instinctive colour choices." },
  { icon: "🔀", label: "Decision Guidance", href: "/quiz/decision",    desc: "Let your subconscious map out the conflict, the risk, and your true desire." },
  { icon: "📚", label: "Colour Library",    href: "/learn",            desc: "175 colours, each with its psychology, symbolism, and cultural meaning." },
  { icon: "🔍", label: "Website Scanner",   href: "/scan",             desc: "Extract and analyse every colour a website uses — with full psychology context." },
];

export default function About() {
  useSeo({
    title: "About | Mychromamind",
    description:
      "Mychromamind is a colour psychology playground built by Daryoush Torabi Jafroudi — a space for exploration, learning, and colour-based tools and games.",
    path: "/about",
  });

  return (
    <Shell>
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-12 md:py-20 w-full">

        {/* ── Hero ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row items-center sm:items-start gap-8 mb-14"
        >
          <img
            src={GRAVATAR}
            alt="Daryoush Torabi Jafroudi"
            width={120}
            height={120}
            loading="lazy"
            decoding="async"
            className="rounded-full w-28 h-28 sm:w-32 sm:h-32 object-cover ring-2 ring-border flex-shrink-0"
          />
          <div className="text-center sm:text-left">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-2">
              Creator
            </p>
            <h1 className="text-3xl md:text-4xl font-serif leading-tight mb-3">
              Daryoush Torabi Jafroudi
            </h1>
            <p className="text-muted-foreground leading-relaxed max-w-md">
              Colour enthusiast, curious mind, and the person behind Mychromamind —
              a playground where colour meets psychology and everyday decisions.
            </p>
          </div>
        </motion.div>

        {/* ── The story ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="space-y-10"
        >
          <section>
            <h2 className="text-xl font-serif mb-4">Why this exists</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Colours have always fascinated me — not just aesthetically, but in the way
                they quietly shape how we feel, decide, and communicate. I wanted a place that
                made that fascinating layer of the world approachable and actually enjoyable to
                explore. Not a textbook. Not a lecture. Just a space where you can poke around,
                learn something surprising, and maybe understand yourself a little better.
              </p>
              <p>
                That's what Mychromamind is. A personal project built out of genuine curiosity —
                equal parts education, entertainment, and experimentation.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-serif mb-4">What you'll find here</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {TOOLS.map(({ icon, label, href, desc }) => (
                <Link
                  key={href}
                  href={href}
                  className="group flex gap-4 p-5 rounded-2xl border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all"
                >
                  <span className="text-2xl mt-0.5 flex-shrink-0">{icon}</span>
                  <div>
                    <p className="font-medium text-sm group-hover:text-primary transition-colors mb-1">
                      {label} →
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-serif mb-4">What's coming</h2>
            <p className="text-muted-foreground leading-relaxed">
              This is just the beginning. My goal is to keep building — more tools, more
              colour-based games, more ways to explore the hidden language of colour in everyday
              life. If you have an idea for something you'd love to see, I'd genuinely love to
              hear it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-serif mb-4">A word on how it was built</h2>
            <p className="text-muted-foreground leading-relaxed">
              I used AI as a collaborator to help bring this to life — writing code, structuring
              content, and handling the technical heavy lifting. But every idea, every feature,
              every "what if we did this?" came from me. The vision is entirely my own; AI was
              the tool that helped make it real faster than I could have alone. I think that's
              an exciting way to build things, and I'm not shy about it.
            </p>
          </section>

          {/* ── CTA ── */}
          <div className="pt-6 border-t border-border/50 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <p className="text-muted-foreground text-sm">
              Got a suggestion, a question, or just want to say hello?
            </p>
            <Link
              href="/contact"
              className="text-sm font-medium px-5 py-2.5 rounded-xl border border-border bg-card hover:border-primary/50 hover:shadow-sm transition-all whitespace-nowrap"
            >
              Get in touch →
            </Link>
          </div>
        </motion.div>
      </div>
    </Shell>
  );
}
